import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { PayPointType, type PayPointBaseDto } from "Infrastructure/Api/Api";
import CircleIcon from "@mui/icons-material/Circle";
import { Box, Stack, Typography } from "@mui/material";
import { Resources, useResource } from "Translations/Resources";

const EMPTY_SPACE_CHAR = "\u00A0";

type Props = {
  paypoint: PayPointBaseDto;
  onClick?: () => void;
};

export const PaypointWidget = ({ paypoint, onClick }: Props) => {
  const { t } = useResource();
  const { name, description, type } = paypoint;
  // Terminal and Gateway are "ready" once they have a linked credential (device / client secret),
  // both surfaced via hasActiveDevice; NFC is ready once its card number is assigned.
  const isPaypointReady =
    paypoint.type === PayPointType.Terminal ||
    paypoint.type === PayPointType.Gateway
      ? paypoint.hasActiveDevice
      : !!paypoint.nfcCardNo;

  // Short badge label per type shown top-right on the /paypoints overview.
  const typeBadgeLabel: Partial<Record<PayPointType, string>> = {
    [PayPointType.Terminal]: t(Resources.Paypoint.Form.TypeTerminal),
    [PayPointType.NFC]: t(Resources.Paypoint.Form.TypeNFC),
    [PayPointType.Gateway]: t(Resources.Paypoint.Form.TypeGatewayShort),
  };
  const badgeLabel = typeBadgeLabel[type];

  return (
    <PrimaryPaper
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        padding: "12px 25px !important",
        minHeight: [80, 100, 120],
        p: [6, 6, 6],
        pt: [6, 6, 6],
        display: "flex",
        alignItems: "center",
        cursor: onClick ? "pointer" : "default",
        border: "1px solid transparent",
        textAlign: "left",
        "&:hover": onClick ? { borderColor: "primary.main" } : {},
      }}
      component="button"
      onClick={onClick}
      tabIndex={onClick ? 0 : -1}
      aria-label={name || ""}
    >
      {badgeLabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          position="absolute"
          right={10}
          top={10}
          sx={{
            px: 1,
            py: 0.25,
            flexShrink: 0,
            borderRadius: 1,
            backgroundColor: "action.hover",
          }}
        >
          {badgeLabel}
        </Typography>
      )}
      <Stack gap={0.5} width="100%" minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <CircleIcon
            sx={({ palette }) => ({
              fontSize: 10,
              flexShrink: 0,
              color: isPaypointReady
                ? palette.success.main
                : palette.warning.main,
            })}
          />
          <Typography variant="h6" noWrap sx={{ minWidth: 0, flexGrow: 1 }}>
            {name}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ minWidth: 0 }}
        >
          {description || EMPTY_SPACE_CHAR}
        </Typography>
      </Stack>
    </PrimaryPaper>
  );
};
